import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  InputNumber,
  Select, 
  Card, 
  Space, 
  Modal, 
  Form, 
  Tag, 
  Typography, 
  Row, 
  Col,
  Alert,
  Spin,
  Tooltip,
  Popconfirm,
  message,
  Avatar,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  FilterOutlined,
  DownloadOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  DollarOutlined,
  TrophyOutlined,
  MedicineBoxOutlined,
  MoreOutlined,
  CloseOutlined,
  SaveOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useDoctors, useCreateDoctor, useUpdateDoctor, useDeleteDoctor, useGraphQLError, useDoctorSpecializations } from '../hooks/useGraphQL';
import { useTranslation } from '../hooks/useTranslation';
import { useNavigate } from 'react-router-dom';
import { getSpecializationOptions, getSpecializationIdMap, getSpecializationLabel } from '../constants/specializations';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

interface Doctor {
  id: string;
  license_number: string;
  first_name_en: string;
  last_name_en: string;
  first_name_zh_hk: string;
  last_name_zh_hk: string;
  first_name_zh_cn: string;
  last_name_zh_cn: string;
  email?: string;
  phone_number?: string;
  specialization: string;
  status: 'active' | 'inactive' | 'suspended';
  rating: number;
  experience_years?: number;
  consultation_fee: number;
  created_at: string;
}

const Doctors: React.FC = () => {
  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  const { data, loading, error } = useDoctors();
  const { data: specializationsData, loading: specializationsLoading } = useDoctorSpecializations(currentLanguage);
  const [createDoctor] = useCreateDoctor();
  const [updateDoctor] = useUpdateDoctor();
  const [deleteDoctor] = useDeleteDoctor();
  const { handleError } = useGraphQLError();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [specializationFilter, setSpecializationFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();

  const specializations = specializationsData?.doctorSpecializations || [];

  const doctors = data?.doctors || [];

  // Throttle search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debug logging for doctors data
  console.log('Current doctors data:', {
    totalDoctors: doctors.length,
    doctors: doctors,
    loading,
    error
  });

  // Get specializations from centralized configuration
  const specializationIdMap = getSpecializationIdMap(currentLanguage);
  const specializationOptions = getSpecializationOptions(currentLanguage);

  // Debug logging for specialization options
  console.log('Specialization options:', {
    originalSpecializations: specializations,
    mappedOptions: specializationOptions,
    idMap: specializationIdMap
  });

  const filteredDoctors = doctors.filter((doctor: Doctor) => {
    const matchesSearch = 
      doctor.first_name_en.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      doctor.last_name_en.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      doctor.first_name_zh_hk.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      doctor.last_name_zh_hk.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      doctor.license_number.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      doctor.specialization.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || doctor.status === statusFilter;
    
    // Fix: Use the raw ID for filtering since specializationFilter now contains raw IDs
    const matchesSpecialization = specializationFilter === 'all' || 
      doctor.specialization === specializationFilter;
    
    // Debug logging for specialization filtering
    if (specializationFilter !== 'all') {
      console.log('Filtering doctor:', {
        doctorName: `${doctor.first_name_en} ${doctor.last_name_en}`,
        doctorSpecialization: doctor.specialization,
        filterValue: specializationFilter,
        matches: matchesSpecialization
      });
    }
    
    return matchesSearch && matchesStatus && matchesSpecialization;
  });

  const handleCreateDoctor = async (values: any) => {
    try {
      setIsSubmitting(true);
      
      // InputNumber components already provide proper numeric types
      const processedValues = {
        ...values,
        tenant: 'default',
        // Ensure Chinese simplified names are populated if not provided
        first_name_zh_cn: values.first_name_zh_cn || values.first_name_zh_hk || '',
        last_name_zh_cn: values.last_name_zh_cn || values.last_name_zh_hk || ''
      };
      
      console.log('Creating doctor with values:', processedValues);
      
      const result = await createDoctor({
        variables: {
          createDoctorDto: processedValues
        }
      });
      
      console.log('Doctor creation result:', result);
      
      setShowCreateModal(false);
      form.resetFields();
      message.success(t('doctors.doctorCreated'));
    } catch (error) {
      console.error('Error creating doctor:', error);
      message.error(handleError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateDoctor = async (id: string, values: any) => {
    try {
      setIsSubmitting(true);
      
      // InputNumber components already provide proper numeric types
      const processedValues = {
        ...values
      };
      
      await updateDoctor({
        variables: {
          id,
          updateDoctorDto: processedValues
        }
      });
      setEditingDoctor(null);
      form.resetFields();
      message.success(t('doctors.doctorUpdated'));
    } catch (error) {
      console.error('Error updating doctor:', error);
      message.error(handleError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditDoctor = (doctor: Doctor) => {
    setEditingDoctor(doctor);
    // Convert the doctor data to form values, ensuring all required fields are handled
    const formValues = {
      ...doctor,
      // Ensure all Chinese name fields are populated
      first_name_zh_hk: doctor.first_name_zh_hk || '',
      last_name_zh_hk: doctor.last_name_zh_hk || '',
      first_name_zh_cn: doctor.first_name_zh_cn || doctor.first_name_zh_hk || '',
      last_name_zh_cn: doctor.last_name_zh_cn || doctor.last_name_zh_hk || '',
      // The specialization is already a raw ID, so no conversion needed
    };
    form.setFieldsValue(formValues);
  };

  const handleDeleteDoctor = async (id: string) => {
    try {
      await deleteDoctor({
        variables: { id }
      });
      message.success(t('doctors.doctorDeleted'));
    } catch (error) {
      console.error('Error deleting doctor:', error);
      message.error(handleError(error));
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'success', text: t('doctors.active'), icon: <CheckCircleOutlined /> },
      inactive: { color: 'warning', text: t('doctors.inactive'), icon: <ExclamationCircleOutlined /> },
      suspended: { color: 'error', text: t('doctors.suspended'), icon: <StopOutlined /> }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={config.color as any} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: t('doctors.doctor'),
      key: 'doctor',
      render: (doctor: Doctor) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size={40}
            style={{ backgroundColor: '#1890ff' }}
          >
            {doctor.first_name_en[0]}{doctor.last_name_en[0]}
          </Avatar>
          <div style={{ marginLeft: 12 }}>
            <div style={{ fontWeight: 500 }}>
              {doctor.first_name_en} {doctor.last_name_en}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {doctor.last_name_zh_hk}{doctor.first_name_zh_hk}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: t('doctors.license'),
      key: 'license',
      render: (doctor: Doctor) => (
        <Text code>{doctor.license_number}</Text>
      ),
    },
    {
      title: t('doctors.specialization'),
      key: 'specialization',
      render: (doctor: Doctor) => (
        <Tag color="blue">{getSpecializationLabel(doctor.specialization, currentLanguage)}</Tag>
      ),
    },
    {
      title: t('doctors.contact'),
      key: 'contact',
      render: (doctor: Doctor) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {doctor.email && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <MailOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
              <Text style={{ fontSize: '12px' }}>{doctor.email}</Text>
            </div>
          )}
          {doctor.phone_number && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <PhoneOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
              <Text style={{ fontSize: '12px' }}>{doctor.phone_number}</Text>
            </div>
          )}
          {!doctor.email && !doctor.phone_number && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('doctors.noContactInfo')}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: t('common.status'),
      key: 'status',
      render: (doctor: Doctor) => getStatusTag(doctor.status),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (doctor: Doctor) => (
        <Space>
          <Tooltip title={t('common.edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                handleEditDoctor(doctor);
              }}
            />
          </Tooltip>
          {/* <Tooltip title={t('schedule.editDoctorSchedule')}>
            <Button
              type="text"
              icon={<ClockCircleOutlined />}
              onClick={() => navigate(`/doctors/${doctor.id}/schedule`)}
            />
          </Tooltip> */}
          <Tooltip title={t('common.view')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
            />
          </Tooltip>
          <Popconfirm
            title={t('doctors.deleteConfirm')}
            onConfirm={() => handleDeleteDoctor(doctor.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('common.delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading || specializationsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('doctors.loadingDoctors')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>{t('doctors.title')}</Title>
            <Text type="secondary">{t('doctors.subtitle')}</Text>
          </Col>
        </Row>
        
        <Card>
          <Alert
            message={t('doctors.errorLoadingDoctors')}
            description={handleError(error)}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }



  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>{t('doctors.title')}</Title>
          <Text type="secondary">{t('doctors.subtitle')}</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setShowCreateModal(true);
              form.resetFields();
            }}
          >
            {t('doctors.addDoctor')}
          </Button>
        </Col>
      </Row>

      

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder={t('doctors.searchDoctors')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('common.status')}
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('doctors.allStatus')}</Option>
              <Option value="active">{t('doctors.active')}</Option>
              <Option value="inactive">{t('doctors.inactive')}</Option>
              <Option value="suspended">{t('doctors.suspended')}</Option>
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('doctors.specialization')}
              value={specializationFilter}
              onChange={setSpecializationFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('doctors.allSpecializations')}</Option>
              {specializationOptions.map((option: { label: string; value: string; key: string }) => (
                <Option key={option.key} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button icon={<FilterOutlined />}>
                {t('doctors.filter')}
              </Button>
              <Button icon={<DownloadOutlined />}>
                {t('doctors.export')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Doctors Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredDoctors}
          rowKey="id"
          pagination={{
            pageSize: 10,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            responsive: true,
            position: ['bottomCenter']
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <DoctorModal
        visible={showCreateModal || !!editingDoctor}
        doctor={editingDoctor}
        specializations={specializations}
        specializationOptions={specializationOptions}
        isSubmitting={isSubmitting}
        form={form}
        onCancel={() => {
          setShowCreateModal(false);
          setEditingDoctor(null);
          form.resetFields();
        }}
        onSubmit={(values) => {
          if (editingDoctor) {
            handleUpdateDoctor(editingDoctor.id, values);
          } else {
            handleCreateDoctor(values);
          }
        }}
      />
    </div>
  );
};

interface DoctorModalProps {
  visible: boolean;
  doctor?: Doctor | null;
  specializations: string[];
  specializationOptions: Array<{ label: string; value: string; key: string }>;
  isSubmitting: boolean;
  form: any;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

const DoctorModal: React.FC<DoctorModalProps> = ({ 
  visible, 
  doctor, 
  specializations, 
  specializationOptions,
  isSubmitting, 
  form, 
  onCancel, 
  onSubmit 
}) => {
  const { t } = useTranslation();

  const handleFormSubmit = (values: any) => {
    console.log('Form submitted with values:', values);
    onSubmit(values);
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <MedicineBoxOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {doctor ? t('doctors.editDoctor') : t('doctors.addDoctor')}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFormSubmit}
        onFinishFailed={(errorInfo) => {
          console.log('Form validation failed:', errorInfo);
        }}
        initialValues={{
          status: 'active',
          experience_years: 0,
          consultation_fee: 0
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('doctors.licenseNumber')}
              name="license_number"
              rules={[{ required: true, message: t('doctors.pleaseInputLicense') }]}
            >
              <Input placeholder={t('doctors.licensePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('doctors.specialization')}
              name="specialization"
              rules={[{ required: true, message: t('doctors.pleaseSelectSpecialization') }]}
            >
              <Select placeholder={t('doctors.selectSpecialization')}>
                {specializationOptions.map((option: { label: string; value: string; key: string }) => (
                  <Option key={option.key} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('doctors.firstNameEn')}
              name="first_name_en"
              rules={[{ required: true, message: t('doctors.pleaseInputFirstName') }]}
            >
              <Input placeholder={t('doctors.firstNamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('doctors.lastNameEn')}
              name="last_name_en"
              rules={[{ required: true, message: t('doctors.pleaseInputLastName') }]}
            >
              <Input placeholder={t('doctors.lastNamePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('doctors.firstNameZh')}
              name="first_name_zh_hk"
              rules={[{ required: true, message: t('doctors.pleaseInputChineseFirstName') }]}
            >
              <Input placeholder={t('doctors.chineseFirstNamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('doctors.lastNameZh')}
              name="last_name_zh_hk"
              rules={[{ required: true, message: t('doctors.pleaseInputChineseLastName') }]}
            >
              <Input placeholder={t('doctors.chineseLastNamePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('doctors.firstNameZhCn')}
              name="first_name_zh_cn"
              rules={[{ required: true, message: t('doctors.pleaseInputChineseSimplifiedFirstName') }]}
            >
              <Input placeholder={t('doctors.chineseSimplifiedFirstNamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('doctors.lastNameZhCn')}
              name="last_name_zh_cn"
              rules={[{ required: true, message: t('doctors.pleaseInputChineseSimplifiedLastName') }]}
            >
              <Input placeholder={t('doctors.chineseSimplifiedLastNamePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('doctors.email')}
              name="email"
              rules={[
                { type: 'email', message: t('doctors.pleaseInputValidEmail') }
              ]}
            >
              <Input placeholder={t('doctors.emailPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('doctors.phoneNumber')}
              name="phone_number"
            >
              <Input placeholder={t('doctors.phonePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('doctors.experienceYears')}
              name="experience_years"
            >
              <InputNumber 
                min={0} 
                max={70}
                precision={0}
                style={{ width: '100%' }}
                placeholder={t('doctors.experiencePlaceholder')} 
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('common.status')}
          name="status"
        >
          <Select>
            <Option value="active">{t('doctors.active')}</Option>
            <Option value="inactive">{t('doctors.inactive')}</Option>
            <Option value="suspended">{t('doctors.suspended')}</Option>
          </Select>
        </Form.Item>

        <div style={{ textAlign: 'right', marginTop: 24 }}>
          <Space>
            <Button onClick={onCancel} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isSubmitting}
              icon={<SaveOutlined />}
            >
              {doctor ? t('doctors.updateDoctor') : t('doctors.createDoctor')}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default Doctors; 